import { useCallback, useEffect, useState } from 'react';
import { cccdAdmin, type CccdAddon } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { Toggle } from '@/components/admin/FormComponents';
import { TrashBinIcon, CheckLineIcon } from '@/icons';
import { toastError, toastSuccess } from '@/lib/toast';
import { showUndoToast } from '@/lib/undoToast';
import { LoadState } from "@/components/ui/LoadState"

const cellInputCls =
  'h-[38px] w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

const PAGE_SIZE = 10;

type EditableField = 'name' | 'priceCop' | 'priceUsd';
type AddonEdits = Partial<Record<EditableField, string | number>>;

function usageLabel(count: number): string {
  if (count === 0) return 'Sin usar';
  if (count === 1) return '1 categoría';
  return `${count} categorías`;
}

// Catálogo global de complementos CCCD (cccd-category-addons-and-pricing):
// nombre y precio se editan una sola vez aquí y se reflejan en toda categoría
// que los tenga habilitados — nunca se redefinen por categoría. Eliminar es
// un soft delete (no borra la fila: preserva los vínculos con categorías y
// el historial de precios ya cobrados), ver requirement "Desactivar un
// complemento no rompe las categorías o equipos que ya lo usan".
export default function AddonsCatalogPage() {
  const [addons, setAddons] = useState<CccdAddon[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [edits, setEdits] = useState<Record<string, AddonEdits>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newPriceCop, setNewPriceCop] = useState('');
  const [newPriceUsd, setNewPriceUsd] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAddons(await cccdAdmin.listAddons());
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al cargar el catálogo de complementos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(addons.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageAddons = addons.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function displayValue(addon: CccdAddon, field: EditableField): string | number {
    return edits[addon.id]?.[field] ?? addon[field];
  }

  function stageEdit(addon: CccdAddon, field: EditableField, raw: string) {
    const value: string | number = field === 'name' ? raw : (Number(raw) || 0);
    setEdits((prev) => {
      const current = { ...(prev[addon.id] ?? {}) };
      if (value === addon[field]) {
        delete current[field];
      } else {
        current[field] = value;
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) delete next[addon.id];
      else next[addon.id] = current;
      return next;
    });
  }

  // Guardar por fila (cccd-admin-design-system, "Catalog rows are
  // inline-editable with per-row actions"): cada fila persiste su propio
  // diff, independiente del resto — no hay "guardar todo".
  async function saveRow(addon: CccdAddon) {
    const patch = edits[addon.id];
    if (!patch) return;
    setSavingId(addon.id);
    try {
      await cccdAdmin.updateAddon(addon.id, patch as never);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[addon.id];
        return next;
      });
      toastSuccess(`"${addon.name}" actualizado.`);
      await load();
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? `No se pudo guardar "${addon.name}".`);
    } finally {
      setSavingId(null);
    }
  }

  async function onCreate() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await cccdAdmin.createAddon({
        name,
        priceCop: Number(newPriceCop) || 0,
        priceUsd: Number(newPriceUsd) || 0,
        sortOrder: addons.length,
      });
      setNewName('');
      setNewPriceCop('');
      setNewPriceUsd('');
      await load();
      setPage(Math.ceil((addons.length + 1) / PAGE_SIZE));
      toastSuccess(`"${name}" agregado al catálogo.`);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al crear el complemento.');
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(addon: CccdAddon) {
    setAddons((prev) => prev.map((a) => (a.id === addon.id ? { ...a, isActive: !a.isActive } : a)));
    try {
      await cccdAdmin.toggleAddon(addon.id, !addon.isActive);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al actualizar el complemento.');
      await load();
    }
  }

  // Elimina de inmediato en la UI y difiere el DELETE (soft delete) real
  // hasta que expire la ventana de "Deshacer" — mismo patrón que
  // EventCategoriesSection/EventPlansSection (design.md, receta "CRUD de lista").
  function handleDelete(addon: CccdAddon) {
    const snapshot = addons;
    setAddons((prev) => prev.filter((a) => a.id !== addon.id));
    showUndoToast({
      message: `Complemento "${addon.name}" eliminado`,
      onUndo: () => setAddons(snapshot),
      onCommit: async () => {
        try {
          await cccdAdmin.deleteAddon(addon.id);
        } catch (err: any) {
          setAddons(snapshot);
          toastError(err?.response?.data?.error ?? 'No se pudo eliminar el complemento.');
        }
      },
    });
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Complementos']}
      title="Catálogo de complementos"
      subtitle="Se definen una sola vez aquí. Cada categoría solo decide cuáles activar y cuál queda preseleccionado — cambiar un precio o nombre se refleja en todas las categorías que lo usan, sin tener que editarlas una por una."
      tabs={[]}
      metrics={[]}
    >
      <div className="mb-5 rounded-2xl border border-dashed border-gray-300 bg-gray-25 p-5 dark:border-gray-700 dark:bg-white/[0.02]">
        <p className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-400">Agregar complemento nuevo</p>
        <div className="grid grid-cols-[2fr_1fr_1fr_auto] items-end gap-3">
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Nombre</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ej. Paracaídas de repuesto"
              className={cellInputCls.replace('h-[38px]', 'h-10')}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Precio COP</label>
            <input
              type="number"
              min={0}
              value={newPriceCop}
              onChange={(e) => setNewPriceCop(e.target.value)}
              placeholder="0"
              className={cellInputCls.replace('h-[38px]', 'h-10')}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Precio USD</label>
            <input
              type="number"
              min={0}
              value={newPriceUsd}
              onChange={(e) => setNewPriceUsd(e.target.value)}
              placeholder="0"
              className={cellInputCls.replace('h-[38px]', 'h-10')}
            />
          </div>
          <button
            type="button"
            onClick={onCreate}
            disabled={!newName.trim() || creating}
            className="h-10 whitespace-nowrap rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {creating ? 'Agregando…' : '+ Agregar'}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto_auto] gap-3 border-b border-gray-100 bg-gray-25 px-5 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
          {['Complemento', 'Precio COP', 'Precio USD', 'Usado en', 'Activo', ''].map((h, i) => (
            <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h}</span>
          ))}
        </div>

        {loading ? (
          <LoadState />
        ) : pageAddons.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin complementos todavía.</p>
        ) : (
          pageAddons.map((addon) => {
            const isDirty = Boolean(edits[addon.id]);
            const isSaving = savingId === addon.id;
            return (
              <div key={addon.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto_auto] items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
                <input
                  type="text"
                  value={displayValue(addon, 'name')}
                  onChange={(e) => stageEdit(addon, 'name', e.target.value)}
                  className={cellInputCls}
                />
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    min={0}
                    value={displayValue(addon, 'priceCop')}
                    onChange={(e) => stageEdit(addon, 'priceCop', e.target.value)}
                    className={`${cellInputCls} pl-5`}
                  />
                </div>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    min={0}
                    value={displayValue(addon, 'priceUsd')}
                    onChange={(e) => stageEdit(addon, 'priceUsd', e.target.value)}
                    className={`${cellInputCls} pl-5`}
                  />
                </div>
                <span className="w-fit rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-500">
                  {usageLabel(addon._count.categoryLinks)}
                </span>
                <Toggle checked={addon.isActive} onChange={() => toggleActive(addon)} label="" />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => saveRow(addon)}
                    disabled={!isDirty || isSaving}
                    title={isDirty ? 'Guardar cambios' : 'Sin cambios'}
                    className={`flex size-7 items-center justify-center rounded-lg border transition-colors ${
                      isDirty
                        ? 'border-brand-100 bg-brand-50 text-brand-500 hover:bg-brand-100 dark:border-brand-500/20 dark:bg-brand-500/15 dark:text-brand-400'
                        : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-600'
                    }`}
                  >
                    <CheckLineIcon className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(addon)}
                    title="Eliminar"
                    className="flex size-7 items-center justify-center rounded-lg border border-gray-100 text-gray-400 hover:bg-error-50 hover:text-error-600 dark:border-gray-800 dark:hover:bg-error-500/10"
                  >
                    <TrashBinIcon className="size-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>Página {currentPage} de {totalPages} · {addons.length} complementos</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </ModuleLayout>
  );
}
